import { FC, useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { StatusModal } from 'src/components/StatusModal'
import { UpgradePlanModal } from 'src/components/UpgradePlanModal'
import { REVIEW_ANALYSIS_CAMPAIGN_ROUTE } from 'src/constants/routes'
import { useCreateReviewAnalysisCampaignMutation } from 'src/reactQuery/useCreateReviewAnalysisCampaignMutation'
import { useGetReviewAnalysisCampaignsQuery } from 'src/reactQuery/useGetReviewAnalysisCampaignsQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import styles from './campaignSelector.module.scss'
import classes from '../reviewAnalysis.module.scss'
import { EmptyState } from '../EmptyState'

const DEFAULT_CAMPAIGN_NAME = 'Review Analysis'

export const CampaignSelector: FC = () => {
  const { data: userFeatures } = useGetUserFeaturesQuery()
  const hasAccess = userFeatures?.data?.reviewAnalysisEnabled ?? false

  const { data: campaigns, isLoading } = useGetReviewAnalysisCampaignsQuery({
    enabled: hasAccess,
  })
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false)
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(
    null
  )
  const [isErrorOpen, setIsErrorOpen] = useState(false)

  const createCampaign = useCreateReviewAnalysisCampaignMutation({
    onError: () => {
      setIsErrorOpen(true)
    },
    onSuccess: (campaign) => {
      setCreatedCampaignId(campaign._id)
    },
  })

  // With no campaigns, create the default one silently and land the user on
  // its add-platform empty state — there is no user-facing "create campaign"
  // step. The ref guards strict-mode double-invoke and refetches while the
  // campaigns list is being invalidated, so only one campaign is ever created.
  const hasTriggeredCreateRef = useRef(false)
  const shouldCreate = hasAccess && !isLoading && campaigns?.length === 0
  useEffect(() => {
    if (!shouldCreate || hasTriggeredCreateRef.current) return
    hasTriggeredCreateRef.current = true
    createCampaign.mutate({ name: DEFAULT_CAMPAIGN_NAME })
  }, [createCampaign, shouldCreate])

  if (!hasAccess) {
    return (
      <>
        <div className={classes.disabledWrapper}>
          <EmptyState
            onEnableClick={() => setIsUpgradeOpen(true)}
            type="disabled"
          />
        </div>
        <UpgradePlanModal isOpen={isUpgradeOpen} setIsOpen={setIsUpgradeOpen} />
      </>
    )
  }

  if (createdCampaignId) {
    return (
      <Navigate
        replace
        to={REVIEW_ANALYSIS_CAMPAIGN_ROUTE.replace(
          ':campaignId',
          createdCampaignId
        )}
      />
    )
  }

  if (campaigns && campaigns.length > 0) {
    return (
      <Navigate
        replace
        to={REVIEW_ANALYSIS_CAMPAIGN_ROUTE.replace(
          ':campaignId',
          campaigns[0]._id
        )}
      />
    )
  }

  return (
    <div className={styles.wrapper}>
      <EmptyState isLoading />
      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={isErrorOpen}
        message="Something went wrong while setting up review analysis. Please try again in a moment."
        onClose={() => setIsErrorOpen(false)}
        onSecondaryAction={() => {
          setIsErrorOpen(false)
          createCampaign.mutate({ name: DEFAULT_CAMPAIGN_NAME })
        }}
        secondaryLabel="Discard"
        title="Setup Couldn't Be Completed"
        type="error"
      />
    </div>
  )
}
